<template>
  <div class="mb-3">
    <label class="form-label">名前</label>
    <input type="text" class="form-control" v-model="admin.name" :disabled="isEdit"/>
  </div>
  <div class="mb-3">
    <label class="form-label">メールアドレス</label>
    <input type="text" class="form-control" v-model="admin.email" :disabled="isEdit"/>
  </div>
  <div class="mb-3">
    <label class="form-label">操作権限</label>
    <div class="d-flex flex-column gap-3 mt-2">
      <!-- Write Permission Toggle -->
      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          role="switch"
          id="writePermission"
          v-model="hasWritePermission"
          @change="updatePermissions"
          :disabled="!hasManagePermissionForEdit"
        >
        <label class="form-check-label" for="writePermission">
          編集権限
          <small class="text-muted d-block">管理者、ユーザーを除くすべてのデータ編集操作が可能</small>
        </label>
      </div>

      <!-- Manage Permission Toggle -->
      <div class="form-check form-switch">
        <input
          class="form-check-input"
          type="checkbox"
          role="switch"
          id="managePermission"
          v-model="hasManagePermission"
          @change="handleManagePermissionChange"
          :disabled="!hasManagePermissionForEdit"
        >
        <label class="form-check-label" for="managePermission">
          管理権限
          <small class="text-muted d-block">管理者、ユーザーのデータの編集操作が可能</small>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import Admin from '../../../models/Admin';
import useFlashMessage from '../../../composables/useFlashMessage';

const {errorMessage} = useFlashMessage();

const props = defineProps({
  modelValue: {
    type: Admin
  },
  isEdit: {
    type: Boolean,
    default: false
  },
  currentAdminId: {
    type: Number,
    default: null
  },
  currentAdminPermissions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue']);

const admin = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
});

// Permission states
const hasWritePermission = ref(false);
const hasManagePermission = ref(false);
const previousManagePermission = ref(false);

// Check if editing self
const isEditingSelf = computed(() => {
  return props.currentAdminId && props.modelValue?.id === props.currentAdminId;
});

// Check if current admin has MANAGE permission
const hasManagePermissionForEdit = computed(() => {
  return props.currentAdminPermissions.some(p => p.permissionType === 'MANAGE');
});

// Initialize permissions from admin data
watch(() => props.modelValue, (newAdmin) => {
  if (newAdmin && newAdmin.permissions) {
    hasWritePermission.value = newAdmin.permissions.some(p => p.permissionType === 'WRITE');
    hasManagePermission.value = newAdmin.permissions.some(p => p.permissionType === 'MANAGE');
    previousManagePermission.value = hasManagePermission.value;
  } else {
    hasWritePermission.value = false;
    hasManagePermission.value = false;
    previousManagePermission.value = false;
  }
}, { immediate: true, deep: true });

// Handle manage permission toggle
const handleManagePermissionChange = () => {
  // Check if trying to remove own manage permission
  if (isEditingSelf.value && previousManagePermission.value && !hasManagePermission.value) {
    // Revert the change
    hasManagePermission.value = true;
    // Show warning message
    errorMessage('ログイン中の管理者ユーザー自身の管理権限を外すことはできません');
    return;
  }

  previousManagePermission.value = hasManagePermission.value;
  updatePermissions();
};

// Update permissions in admin model
const updatePermissions = () => {
  const permissions = [];
  if (hasWritePermission.value) {
    permissions.push({ permissionType: 'WRITE' });
  }
  if (hasManagePermission.value) {
    permissions.push({ permissionType: 'MANAGE' });
  }

  admin.value = {
    ...admin.value,
    permissions: permissions
  };
};
</script>
